// 종가 시계열(오름차순) 기반 기본 기술적 신호. 0토큰 클라이언트 계산.
// 엔벨로프(20MA±10%) · 이평선(20/60/120 터치·돌파) · 채널(Donchian 20일) · 추세선(60일 회귀 ±2σ).

function sma(c, n, i) {
  if (i - n + 1 < 0) return null;
  let s = 0;
  for (let k = i - n + 1; k <= i; k++) s += c[k];
  return s / n;
}

// 60일 로그종가 선형회귀 잔차: 마지막 관측이 +2σ↑ → 상향돌파(1), −2σ↓ → 하향이탈(−1), else 0
function regBreak(seg) {
  const n = seg.length;
  const y = seg.map(Math.log);
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (let k = 0; k < n; k++) { sx += k; sy += y[k]; sxx += k * k; sxy += k * y[k]; }
  const denom = n * sxx - sx * sx;
  if (denom === 0) return 0;
  const b = (n * sxy - sx * sy) / denom;
  const a = (sy - b * sx) / n;
  const resid = y.map((yi, k) => yi - (a + b * k));
  const mean = resid.reduce((s, r) => s + r, 0) / n;
  const sd = Math.sqrt(resid.reduce((s, r) => s + (r - mean) * (r - mean), 0) / n) || 1e-9;
  const last = resid[n - 1];
  if (last > 2 * sd) return 1;
  if (last < -2 * sd) return -1;
  return 0;
}

// closes: 종가 배열(오름차순). 신호 없으면 sigs=[]. 데이터 부족(60봉 미만)이면 null.
export function computeSignals(closes) {
  const c = (closes || []).filter((x) => x > 0);
  const n = c.length;
  if (n < 60) return null;
  const i = n - 1, prev = n - 2;
  const price = c[i];
  const sigs = [];

  // 1) 엔벨로프 (20일 MA ±10%)
  const ma20 = sma(c, 20, i);
  if (ma20) {
    if (price >= ma20 * 1.1) sigs.push({ t: "엔벨 상단이탈", dir: "up" });
    else if (price <= ma20 * 0.9) sigs.push({ t: "엔벨 하단이탈", dir: "down" });
  }

  // 2) 이평선 (20/60/120) 돌파 우선, 아니면 ±1.5% 터치
  for (const p of [20, 60, 120]) {
    const ma = sma(c, p, i), maPrev = sma(c, p, prev);
    if (ma == null || maPrev == null) continue;
    if (c[prev] < maPrev && price > ma) sigs.push({ t: `${p}일선 상향돌파`, dir: "up" });
    else if (c[prev] > maPrev && price < ma) sigs.push({ t: `${p}일선 하향이탈`, dir: "down" });
    else if (Math.abs((price - ma) / ma) <= 0.015) sigs.push({ t: `${p}일선 터치`, dir: "flat" });
  }

  // 3) 채널 돌파 (직전 20봉 Donchian 신고/신저)
  const win = c.slice(i - 20, i);
  if (win.length === 20) {
    const hi = Math.max(...win), lo = Math.min(...win);
    if (price > hi) sigs.push({ t: "20일 채널 신고가", dir: "up" });
    else if (price < lo) sigs.push({ t: "20일 채널 신저가", dir: "down" });
  }

  // 4) 추세선 (60일 회귀 ±2σ 돌파)
  const tl = regBreak(c.slice(n - 60));
  if (tl > 0) sigs.push({ t: "추세선 상향돌파", dir: "up" });
  else if (tl < 0) sigs.push({ t: "추세선 하향이탈", dir: "down" });

  return { price, sigs };
}

// 신규 발생 신호만: 최근 LOOK 거래일 안에 "없다가 생긴" 신호. ago=0 → 오늘 신규(불빛).
// 이미 LOOK일 내내 켜져있던(=오래전 돌파한) 상태는 제외 — 타이밍 도구.
const LOOK = 6;
export function freshSignals(closes) {
  const c = (closes || []).filter((x) => x > 0);
  if (c.length < 60) return null;
  const sets = [];
  for (let w = 0; w <= LOOK; w++) {
    if (c.length - w < 60) { sets.push(new Map()); continue; }
    const r = computeSignals(c.slice(0, c.length - w));
    sets.push(r ? new Map(r.sigs.map((s) => [s.t, s.dir])) : new Map());
  }
  const out = [];
  for (const [t, dir] of sets[0]) {
    let ago = 0;
    while (ago < LOOK && sets[ago + 1].has(t)) ago++;
    if (ago < LOOK) out.push({ t, dir, ago });   // ago==LOOK → 계속 켜져있던 오래된 상태, 제외
  }
  out.sort((a, b) => a.ago - b.ago);
  return out;
}
